import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import SeoHead from '../../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://www.cherokeeyouthwarriors.com';

const cheerSchema = {
  '@context': 'https://schema.org',
  '@type': 'SportsEvent',
  name: 'Cherokee Youth Cheer Program 2026 Season',
  description: 'K-5 youth cheerleading program in Canton, GA. Sideline cheer and competition cheer squads with uniforms provided, weekly practices, and performance opportunities.',
  url: `${siteUrl}/programs/cheer`,
  location: {
    '@type': 'Place',
    name: 'Canton, Georgia',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Canton',
      addressRegion: 'GA',
      addressCountry: 'US',
    },
  },
  organizer: {
    '@type': 'SportsOrganization',
    name: 'Cherokee Youth Football & Cheer',
    url: siteUrl,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Cheer Program', item: `${siteUrl}/programs/cheer` },
    ],
  },
};

const gradeLevels = [
  { id: 1, grade: 'K1', coach: 'To Be Determined' },
  { id: 2, grade: '2nd Grade', coach: 'To Be Determined' },
  { id: 3, grade: '3rd Grade', coach: 'To Be Determined' },
  { id: 4, grade: '4th Grade', coach: 'To Be Determined' },
  { id: 5, grade: '5th Grade', coach: 'To Be Determined' },
];

const programDetails = [
  {
    id: 1,
    title: 'Uniform Provided',
    items: ['Cheer uniform', 'Bloomers', 'Poms', 'Bows'],
  },
  {
    id: 2,
    title: 'Practice Schedule',
    items: ['2 times per week (Tues/Thurs)', '60-90 minute sessions', 'Weekday evenings', 'Choreography & stunts', 'Located at Teasley Middle or New Cherokee High'],
  },
  {
    id: 3,
    title: 'Performance Schedule',
    items: ['Cheer at all youth football games', 'Competition opportunities', 'On-field opportunities on Friday nights', 'Homecoming'],
  },
];

export default function CheerPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SeoHead
        title="Youth Cheer Program Canton GA | Cherokee Warriors K-5"
        description="Cherokee Youth Cheer program in Canton, GA offers K-5 sideline and competition cheer squads. Uniforms provided, weekly practices, and performance opportunities. Register for 2026."
        keywords="youth cheerleading Canton GA, Cherokee Warriors cheer, K-5 cheer program, competition cheer Georgia, youth sideline cheer"
        canonical={`${siteUrl}/programs/cheer`}
        schemaJson={cheerSchema}
      />
      <Header />

      <main className="flex-grow">
        <section className="relative py-24" style={{ backgroundImage: 'url(https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/5cf78a922e2bb70b484b9846b1c690e2.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center 30%' }}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white font-sans text-sm mb-8 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl text-white leading-none">
              CHEER<br />PROGRAM
            </h1>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-5xl text-warrior-black mb-4">OUR TEAMS</h2>
              <p className="font-sans text-lg text-gray-600">Five competitive cheer squads for all grade levels</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {gradeLevels.map((level) => (
                <div key={level.id} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="w-16 h-16 bg-warrior-red rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-trophy-line text-3xl text-white"></i>
                  </div>
                  <h3 className="font-display text-2xl text-warrior-black text-center mb-3">{level.grade}</h3>
                  <div className="text-center">
                    <p className="font-sans text-sm text-gray-500 mb-1">Head Coach</p>
                    <p className="font-sans text-sm font-semibold text-gray-700">{level.coach}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-5xl text-warrior-black mb-4">PROGRAM DETAILS</h2>
              <p className="font-sans text-lg text-gray-600">Everything included in your cheer experience</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {programDetails.map((detail) => (
                <div key={detail.id} className="bg-gray-100 rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="font-display text-3xl text-warrior-black mb-6 text-center">{detail.title}</h3>
                  <ul className="space-y-3">
                    {detail.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-warrior-red text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                        <span className="font-sans text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-5xl text-warrior-black mb-4">COMPETITION CHEER</h2>
              <p className="font-sans text-lg text-gray-600">Take your skills to the next level</p>
            </div>
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="relative h-64 md:h-auto">
                  <img src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/2452e47dea10cf23898736b9e42a4eae.jpeg" alt="Competition Cheer" className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
                <div className="p-8 flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-warrior-red/10 text-warrior-red font-sans text-xs font-semibold px-3 py-1 rounded-full mb-4">
                      <i className="ri-trophy-fill"></i>
                      SEPARATE REGISTRATION
                    </div>
                    <h3 className="font-display text-4xl text-warrior-black mb-4">COMPETE &amp; EXCEL</h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed mb-6">
                      Our Competition Cheer program, led by our varsity cheer coach, Michelle Duren, is designed for athletes who want to push beyond sideline cheering and compete at regional and state-level events. Advanced stunting, tumbling, and choreography are at the core of this elite squad.
                    </p>
                    <ul className="space-y-2 mb-6">
                      {['Advanced stunts & tumbling','Regional & state competitions','Dedicated competition coaching','Performance choreography'].map((item, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <i className="ri-checkbox-circle-fill text-warrior-red text-lg w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                          <span className="font-sans text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-warrior-red">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-event-fill text-warrior-red text-xl"></i>
                      </div>
                      <div>
                        <p className="font-display text-sm text-warrior-black tracking-wide">REGISTRATION OPENS</p>
                        <p className="font-display text-2xl text-warrior-red leading-tight">MARCH 15TH</p>
                        <p className="font-sans text-xs text-gray-500 mt-1">
                          Competition Cheer registration is <strong>separate</strong> from the standard cheer program. Check back on March 15th to sign up.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-warrior-red via-red-600 to-red-700 relative">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-5xl sm:text-6xl text-white mb-6">
              READY TO JOIN<br />THE youth sideline SQUAD?
            </h2>
            <p className="font-sans text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Register now for the 2026 season and become part of the Warriors cheer family!
            </p>
            <a
              href="https://cobbfootball.leagueapps.com/clubteams/4926021-2026-cherokee-youth-football--cheerleading"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-warrior-red font-display text-xl px-12 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              REGISTER NOW
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
